import axios from "./axios";
import {
  buildDefaultAdminSignatureSection,
  stripPolicySignatureLines,
  type PolicySection,
} from "./companySafetyPolicyContent";

export interface CompanySafetyPolicySignature {
  signerName: string;
  signerTitle?: string;
  signedAt: string;
  policyVersion: number;
}

export interface CompanySafetyPolicyRecord {
  version: number;
  sections: PolicySection[];
  adminSignatureSection: PolicySection;
  signature: CompanySafetyPolicySignature | null;
  updatedAt: string;
  companyId?: string | number;
}

export interface SignCompanySafetyPolicyInput {
  signerName: string;
  signerTitle?: string;
  policyVersion: number;
}

export class PolicyVersionConflictError extends Error {
  constructor(message = "Policy version mismatch. Reload and sign again.") {
    super(message);
    this.name = "PolicyVersionConflictError";
  }
}

const normalizeSections = (sections: PolicySection[]): PolicySection[] =>
  sections.map((section) =>
    section.id === "acknowledgement"
      ? {
          ...section,
          paragraphs: stripPolicySignatureLines(section.paragraphs ?? []),
        }
      : section
  );

const normalizeSignature = (
  raw: Record<string, unknown> | null | undefined
): CompanySafetyPolicySignature | null => {
  if (!raw || typeof raw !== "object") return null;

  const signerName = String(raw.signerName ?? raw.signer_name ?? "").trim();
  if (!signerName) return null;

  const signerTitle = String(raw.signerTitle ?? raw.signer_title ?? "").trim();
  const signedAt = String(raw.signedAt ?? raw.signed_at ?? "");
  const policyVersion = Number(raw.policyVersion ?? raw.policy_version ?? 0);

  return {
    signerName,
    ...(signerTitle ? { signerTitle } : {}),
    signedAt,
    policyVersion,
  };
};

const normalizeRecord = (
  raw: Record<string, unknown> | null | undefined,
  companyName = "Your Company"
): CompanySafetyPolicyRecord | null => {
  if (!raw || typeof raw !== "object") return null;

  const sections = (raw.sections as PolicySection[] | undefined) ?? [];
  if (!sections.length) return null;

  const adminRaw =
    (raw.adminSignatureSection as PolicySection | undefined) ??
    (raw.admin_signature_section as PolicySection | undefined);

  const companyId = raw.companyId ?? raw.company_id;

  return {
    version: Number(raw.version ?? 1),
    sections: normalizeSections(sections),
    adminSignatureSection:
      adminRaw ?? buildDefaultAdminSignatureSection(companyName),
    signature: normalizeSignature(
      raw.signature as Record<string, unknown> | null | undefined
    ),
    updatedAt: String(
      raw.updatedAt ?? raw.updated_at ?? new Date().toISOString()
    ),
    ...(companyId !== undefined && companyId !== null
      ? { companyId: companyId as string | number }
      : {}),
  };
};

/** Envelope: { status, message, data } */
const unwrapData = (res: any): Record<string, unknown> | null => {
  const data = res?.data?.data;
  if (data === null || data === undefined) return null;
  if (typeof data !== "object") return null;
  if ("sections" in data || "version" in data) {
    return data as Record<string, unknown>;
  }
  return null;
};

const apiMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback;

/** GET /omw/company-safety-policy — company from auth token */
export const fetchCompanySafetyPolicy = async (
  companyName = "Your Company"
): Promise<CompanySafetyPolicyRecord | null> => {
  const res = await axios({
    URL: "/omw/company-safety-policy",
    method: "GET",
  });

  // data: null → no row yet; frontend keeps defaults in UI
  if (res?.data?.data == null) return null;

  return normalizeRecord(unwrapData(res), companyName);
};

/** PUT /omw/company-safety-policy — upsert sections (version++, signature null) */
export const saveCompanySafetyPolicySections = async (
  sections: PolicySection[],
  companyName = "Your Company"
): Promise<CompanySafetyPolicyRecord> => {
  const res = await axios({
    URL: "/omw/company-safety-policy",
    method: "PUT",
    data: {
      sections: normalizeSections(sections),
    },
  });

  const record = normalizeRecord(unwrapData(res), companyName);
  if (!record) {
    throw new Error("Failed to save company safety policy sections");
  }
  return record;
};

/**
 * PUT /omw/company-safety-policy/admin-signature-section
 * Does not bump version / clear signature.
 */
export const saveAdminSignatureSection = async (
  adminSignatureSection: PolicySection,
  companyName = "Your Company"
): Promise<CompanySafetyPolicyRecord> => {
  const res = await axios({
    URL: "/omw/company-safety-policy/admin-signature-section",
    method: "PUT",
    data: {
      adminSignatureSection,
    },
  });

  const record = normalizeRecord(unwrapData(res), companyName);
  if (!record) {
    throw new Error("Failed to save admin signature section");
  }
  return record;
};

/** First sign with no saved row → PUT full sections (creates), then caller POSTs sign */
export const ensureCompanySafetyPolicyRecord = async (
  sections: PolicySection[],
  current?: CompanySafetyPolicyRecord | null,
  companyName = "Your Company"
): Promise<CompanySafetyPolicyRecord> => {
  if (current) return current;
  return saveCompanySafetyPolicySections(sections, companyName);
};

/** POST /omw/company-safety-policy/sign */
export const signCompanySafetyPolicy = async (
  input: SignCompanySafetyPolicyInput,
  companyName = "Your Company"
): Promise<CompanySafetyPolicyRecord> => {
  try {
    const res = await axios({
      URL: "/omw/company-safety-policy/sign",
      method: "POST",
      data: {
        signerName: input.signerName.trim(),
        signerTitle: input.signerTitle?.trim() || undefined,
        policyVersion: input.policyVersion,
        acknowledged: true,
      },
    });

    const record = normalizeRecord(unwrapData(res), companyName);
    if (!record) {
      throw new Error("Failed to sign company safety policy");
    }
    return record;
  } catch (error: any) {
    if (error?.response?.status === 409) {
      throw new PolicyVersionConflictError(
        apiMessage(error, "Policy version mismatch. Reload and sign again.")
      );
    }
    throw error;
  }
};
